// import { watch } from 'vue'
import { defineStore } from 'pinia'
import PromiseQueue from '../promiseQueue'

let uploaderIdIndex = 0

const uploaders = new Map()

const state = () => ({
  uploadersData: new Map(),
})

// functionName: (state, getters, rootState) => {}
const getters = {
  currentFileProgress() {
    return (uploaderId) => {
      const currentUploadersFile = this.currentUploadedFile(uploaderId)

      if (currentUploadersFile === null || currentUploadersFile.parts.length === 0) {
        return 0
      }

      let loadedAcc = 0

      currentUploadersFile.parts.forEach(part => {
        loadedAcc += part.loaded
      })

      return Math.round(loadedAcc / currentUploadersFile?.size * 100)
    }
  },
  currentFileIsPaused() {
    return (uploaderId) => {
      const currentUploadersFile = this.currentUploadedFile(uploaderId)

      if (currentUploadersFile === null || currentUploadersFile.parts.length === 0) {
        return false
      }

      return currentUploadersFile.parts.some(part => part.isPaused && part.shouldRunning)
    }
  },
  currentUploaderShouldRunning() {
    return (uploaderId) => {
      const uploaderData = this.uploadersData.get(uploaderId)

      if (!Number.isInteger(uploaderData?.currentFileIndex)) {
        return
      }

      let isRunning = false

      for (let index = uploaderData.currentFileIndex; index < uploaderData.files.length; index++) {
        if (uploaderData.files[index].parts.length === 0 || uploaderData.files[index].parts.some(part => part.shouldRunning)) {
          isRunning = true
          break
        }
      }

      return isRunning
    }
  },
  currentUploadedFile() {
    return (uploaderId) => {
      const uploaderData = this.uploadersData.get(uploaderId)

      if (!Number.isInteger(uploaderData?.currentFileIndex)) {
        return null
      }

      return uploaderData.files[uploaderData.currentFileIndex]
    }
  },
  listUploadedFiles() {
    return (uploaderId) => {
      return this.uploadersData.get(uploaderId).files.map((file) => file.bcFile)
    }
  },
  listCurrentUploadedFilePartsEtags() {
    return (uploaderId) => {
      const currentUploadersFile = this.currentUploadedFile(uploaderId)
      return currentUploadersFile.parts.map((part) => part.etag)
    }
  }
}

// functionName ({ commit, state, dispatch }, param, ...) {}
const actions = {
  mountUploader(config) {
    const uploaderId = !config.uploaderId
      ? 'uploader' + uploaderIdIndex++
      : config.uploaderId

    if (uploaders.has(uploaderId)) {
      this.updateUploaderConfig(uploaderId, config)
      return uploaderId
    }

    const queue = new PromiseQueue({
      concurrent: 1,
      interval: 100,
      start: false,
    })

    uploaders.set(uploaderId, {
      config,
      queue,
    })

    this.uploadersData.set(uploaderId, {
      currentFileIndex: null, // currently uploading file
      isRunning: false,
      isMounted: true,
      files: [/*
        {
          file: fileObject, // file object to be uploaded
          name: '', // file name
          size: 0, // file size,
          parts: [ // list of parts of file to be uploaded
            {
              url: '', // url to be used to upload file part
              cancelToken: cancelationToken, // file upload cancelation token or null
              start: () => {}, // callback to start upload
              pause: () => {}, // callback to pause upload
              isPaused: true/false,
              loaded: 0,
              shouldRunning: true/false,
              uploadDonePromise: null,
              etag: null,
            }
          ],
          bcFile: null, // uploaded file data
        }
      */], 
    })

    return uploaderId
  },
  unmountUploader(uploaderId) {
    if (!this.currentUploaderShouldRunning(uploaderId)) {
      this.uploadersData.delete(uploaderId)
      uploaders.delete(uploaderId)
      return true
    }

    return false
  },
  updateUploaderConfig(uploaderId, config) {
    console.log('updateUploaderConfig ' + uploaderId, config)
    const currentConfig = uploaders.get(uploaderId).config
    Object.keys(config).forEach((key) => {
      currentConfig[key] = config[key]
    })
  },
  pauseUpload(uploaderId) {
    const uploaderData = this.uploadersData.get(uploaderId)
    uploaderData.files[uploaderData.currentFileIndex].parts.forEach((part) => {
      if (part.shouldRunning) {
        part.pause()
      }
    })
  },
  resumeUpload(uploaderId) {
    const uploaderData = this.uploadersData.get(uploaderId)
    uploaderData.files[uploaderData.currentFileIndex].parts.forEach((part) => {
      if (part.shouldRunning) {
        part.start()
      }
    })
  },
  stopUpload(uploaderId) {
    this.pauseUpload(uploaderId)
    this.resetUploaderFiles(uploaderId)
  },
  resetUploaderFiles(uploaderId) {
    const uploaderData = this.uploadersData.get(uploaderId)
    uploaderData.currentFileIndex = null
    uploaderData.files = []
    uploaderData.isRunning = false
    uploaders.get(uploaderId).queue.stop()
    uploaders.get(uploaderId).queue.clear()
  },
  getUploaderConfig(uploaderId) {
    return uploaders.get(uploaderId)?.config
  },
  getUploaderQueue(uploaderId) {
    return uploaders.get(uploaderId).queue
  },
  getUploaderData(uploaderId) {
    return this.uploadersData.get(uploaderId)
  },
  addFileToUpload(uploaderId, fileObject) {
    return this.uploadersData.get(uploaderId).files.push({
      file: fileObject, // file object to be uploaded
      name: fileObject.name, // file name
      size: fileObject.size, // file size,
      parts: [ // list of parts of file to be uploaded
        /*
        {
          url: '', // url to be used to upload file part
          cancelation token: cancelationToken, // file upload cancelation token or null
          start: () => {}, // callback to start upload
          pause: () => {}, // callback to pause upload,
          isPaused: true/false,
          loaded: 0,
          shouldRunning: true/false,
          uploadDonePromise: null,
          etag: null,
        }
        */
      ],
      bcFile: null, // uploaded file data
    }) - 1
  },
  startFileUpload(uploaderId, uploaderFileIndex) {
    this.uploadersData.get(uploaderId).currentFileIndex = uploaderFileIndex
  },
  addFileUploadPart(uploaderId, uploaderFileIndex, part) {
    this.uploadersData.get(uploaderId).files[uploaderFileIndex].parts.push(part)
  }
}

export default defineStore({
  id: 'bcUploader',
  state,
  getters,
  actions
})
